import { Component, OnDestroy, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterOutlet, NavigationCancel, NavigationEnd, NavigationError, NavigationStart } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { ApiService } from './shared/services/api.service';
import { BenchService } from './shared/services/bench.service';
import { ProvidersCacheService } from './shared/services/providers-cache.service';
import { SlotsCacheService } from './shared/services/slots-cache.service';
import { VariantAuditService } from './shared/services/variant-audit.service';
import { VariantService } from './shared/services/variant.service';
import { InsuranceVariantService } from './shared/services/insurance-variant.service';
import { PartnerProviderContextService } from './shared/services/partner-provider-context.service';
import { MemoTagContextService } from './shared/services/memo-tag-context.service';
import { getAllowedVisitTypeCodesForState, mapVisitTypeCodeToLabel, STATE_VISIT_CONFIG } from './shared/data/state-visit-config';
import { Loader } from './shared/loader/loader';

const STATE_NEIGHBORS: Record<string, string[]> = {
  'AL': ['FL', 'GA', 'MS', 'TN'],
  'AK': [],
  'AZ': ['CA', 'NV', 'NM', 'UT'],
  'AR': ['LA', 'MO', 'MS', 'OK', 'TN', 'TX'],
  'CA': ['AZ', 'NV', 'OR'],
  'CO': ['KS', 'NE', 'NM', 'OK', 'UT', 'WY'],
  'CT': ['MA', 'NY', 'RI'],
  'DE': ['MD', 'NJ', 'PA'],
  'FL': ['AL', 'GA'],
  'GA': ['AL', 'FL', 'NC', 'SC', 'TN'],
  'HI': [],
  'ID': ['MT', 'NV', 'OR', 'UT', 'WA', 'WY'],
  'IL': ['IN', 'IA', 'KY', 'MO', 'WI'],
  'IN': ['IL', 'KY', 'MI', 'OH'],
  'IA': ['IL', 'MN', 'MO', 'NE', 'SD', 'WI'],
  'KS': ['CO', 'MO', 'NE', 'OK'],
  'KY': ['IL', 'IN', 'MO', 'OH', 'TN', 'VA', 'WV'],
  'LA': ['AR', 'MS', 'TX'],
  'ME': ['NH'],
  'MD': ['DE', 'PA', 'VA', 'WV'],
  'MA': ['CT', 'NH', 'NY', 'RI', 'VT'],
  'MI': ['IN', 'OH', 'WI'],
  'MN': ['IA', 'ND', 'SD', 'WI'],
  'MS': ['AL', 'AR', 'LA', 'TN'],
  'MO': ['AR', 'IL', 'IA', 'KS', 'KY', 'NE', 'OK', 'TN'],
  'MT': ['ID', 'ND', 'SD', 'WY'],
  'NE': ['CO', 'IA', 'KS', 'MO', 'SD', 'WY'],
  'NV': ['AZ', 'CA', 'ID', 'OR', 'UT'],
  'NH': ['MA', 'ME', 'VT'],
  'NJ': ['DE', 'NY', 'PA'],
  'NM': ['AZ', 'CO', 'OK', 'TX', 'UT'],
  'NY': ['CT', 'MA', 'NJ', 'PA', 'VT'],
  'NC': ['GA', 'SC', 'TN', 'VA'],
  'ND': ['MN', 'MT', 'SD'],
  'OH': ['IN', 'KY', 'MI', 'PA', 'WV'],
  'OK': ['AR', 'CO', 'KS', 'MO', 'NM', 'TX'],
  'OR': ['CA', 'ID', 'NV', 'WA'],
  'PA': ['DE', 'MD', 'NJ', 'NY', 'OH', 'WV'],
  'RI': ['CT', 'MA'],
  'SC': ['GA', 'NC'],
  'SD': ['IA', 'MN', 'MT', 'ND', 'NE', 'WY'],
  'TN': ['AL', 'AR', 'GA', 'KY', 'MO', 'MS', 'NC', 'VA'],
  'TX': ['AR', 'LA', 'NM', 'OK'],
  'UT': ['AZ', 'CO', 'ID', 'NV', 'NM', 'WY'],
  'VT': ['MA', 'NH', 'NY'],
  'VA': ['KY', 'MD', 'NC', 'TN', 'WV'],
  'WA': ['ID', 'OR'],
  'WV': ['KY', 'MD', 'OH', 'PA', 'VA'],
  'WI': ['IL', 'IA', 'MI', 'MN'],
  'WY': ['CO', 'ID', 'MT', 'NE', 'SD', 'UT']
};

@Component({
  selector: 'app-root',
  imports: [CommonModule, RouterOutlet, Loader],
  templateUrl: './app.html',
  styleUrl: './app.css'
})
export class App implements OnInit, OnDestroy {
  protected readonly title = signal('OLS_GUI');
  protected readonly variantReady = signal(false);

  private routerSubscription: Subscription | null = null;
  private firstRouteHandled = false;
  private abSessionEnabled = false;
  private pendingNavEnd: NavigationEnd | null = null;
  private variantInitStarted = false;
  private initialVariantEventsPushed = false;
  private partnerInitPromise: Promise<void> | null = null;
  private memoTagInitPromise: Promise<void> | null = null;
  private readonly phoneClickListener = (event: Event) => {
    try {
      if ((event as any).__phoneClickTracked) return;
    } catch {}
    try {
      const target = event.target as Element | null;
      if (!target || typeof (target as any).closest !== 'function') return;
      const anchor = target.closest('a[href^="tel:"]') as HTMLAnchorElement | null;
      if (!anchor) return;
      (event as any).__phoneClickTracked = true;
      const href = String(anchor.getAttribute('href') || '').trim();
      const phone = href.replace(/^tel:/i, '').trim();
      const text = String(anchor.textContent || '').trim();
      const dataLayer = (window as any).dataLayer = (window as any).dataLayer || [];
      dataLayer.push({
        event: 'phone_call_click',
        phone_number: phone,
        click_text: text,
        click_source: 'tel_anchor'
      });
    } catch {}
  };
  constructor(
    private readonly router: Router,
    private readonly api: ApiService,
    private readonly bench: BenchService,
    private readonly providersCache: ProvidersCacheService,
    private readonly slotsCache: SlotsCacheService,
    private readonly variantAudit: VariantAuditService,
    private readonly variantService: VariantService,
    private readonly insuranceVariantService: InsuranceVariantService,
    private readonly partnerProviderContext: PartnerProviderContextService,
    private readonly memoTagContext: MemoTagContextService
  ) {}

  private isInternalUser(): boolean {
    try {
      const cookie = (typeof document !== 'undefined' && typeof document.cookie === 'string') ? document.cookie : '';
      if (cookie && /(?:^|;\s*)internal_user=true(?:;|$)/i.test(cookie)) return true;
    } catch {}

    try {
      const params = new URLSearchParams(window.location.search || '');
      const v = (params.get('internal_user') || '').toString().trim().toLowerCase();
      if (v === 'true') return true;
    } catch {}

    try {
      const href = (typeof window !== 'undefined' && window.location && window.location.href) ? String(window.location.href) : '';
      if (href.toLowerCase().includes('internal_user=true')) return true;
    } catch {}

    return false;
  }
   
  private applyInternalUserCookieFromUrl(): void {
    try {
      const href = (typeof window !== 'undefined' && window.location && window.location.href) ? String(window.location.href) : '';
      if (!href || href.indexOf('internal_user=true') === -1) return;
      try {
        document.cookie = `internal_user=true; max-age=${365 * 24 * 60 * 60}; path=/`;
      } catch {}
    } catch {}
  }

  private parseAbTests(raw: string | null | undefined): Array<{ event: string; key: string; value: string }> {
    try {
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      const out: Array<{ event: string; key: string; value: string }> = [];
      for (const t of parsed) {
        const ev = ((t as any)?.event ?? '').toString().trim();
        const k = ((t as any)?.key ?? '').toString().trim();
        const v = ((t as any)?.value ?? '').toString().trim();
        if (ev === 'MENU_AB_Test' && k === 'menu_variant') continue;
        if (ev && k && v) out.push({ event: ev, key: k, value: v });
      }
      return out;
    } catch {
      return [];
    }
  }

  private readAbTestsFromUrl(): Array<{ event: string; key: string; value: string }> {
    try {
      const params = new URLSearchParams(window.location.search || '');
      const raw = params.get('ab_tests');
      if (!raw) return [];
      return this.parseAbTests(raw);
    } catch {
      return [];
    }
  }

  private readAbTestsFromSession(): Array<{ event: string; key: string; value: string }> {
    try {
      const raw = sessionStorage.getItem('ab_tests');
      if (!raw) return [];
      return this.parseAbTests(raw);
    } catch {
      return [];
    }
  }

  private storeAbTestsToSession(tests: Array<{ event: string; key: string; value: string }>): void {
    try {
      if (!tests || tests.length === 0) return;
      try { sessionStorage.setItem('ab_tests', JSON.stringify(tests)); } catch {}
      for (const t of tests) {
        try { sessionStorage.setItem(String(t.key), String(t.value)); } catch {}
      }
    } catch {}
  }

  private getAbTestsQueryValueFromSession(): string {
    try {
      const tests = this.readAbTestsFromSession();
      if (!tests || tests.length === 0) return '';
      return JSON.stringify(tests);
    } catch {
      return '';
    }
  }

  private ensureAbTestsQueryParamInUrl(): void {
    try {
      if (typeof window === 'undefined' || !window.location || !window.history) return;
      const queryValue = this.getAbTestsQueryValueFromSession();
      if (!queryValue) return;
      const url = new URL(window.location.href);
      const current = (url.searchParams.get('ab_tests') || '').trim();
      if (current) return;
      url.searchParams.set('ab_tests', queryValue);
      const next = `${url.pathname}${url.search}${url.hash || ''}`;
      window.history.replaceState(window.history.state, '', next);
    } catch {}
  }

  private clearAbTestsFromSession(): void {
    try {
      const existing = this.readAbTestsFromSession();
      for (const t of existing) {
        try { sessionStorage.removeItem(String(t.key)); } catch {}
      }
      try { sessionStorage.removeItem('ab_tests'); } catch {}
      try { sessionStorage.removeItem('ab_tests.pushed.index'); } catch {}
    } catch {}
  }

  private wasPushedFromIndex(): boolean {
    try {
      // Guard variable set in index.html, or storage flag
      const fromGlobal = !!(window as any).__abTestsPushedFromIndex;
      const fromStorage = sessionStorage.getItem('ab_tests.pushed.index') === '1';
      return fromGlobal || fromStorage;
    } catch {
      return false;
    }
  }

  private extractInsuranceAbTestVariant(assignment: any): string {
    const code = ((assignment?.version_code || '') + '').trim().toLowerCase().replace(/-/g, '_');
    if (code === 'cards') return 'cards';
    const name = ((assignment?.version_name || '') + '').trim();
    if (name) return name;
    return 'scroll';
  }

  private wasInsuranceAbTestPushedFromIndex(): boolean {
    try {
      return !!(window as any).__olsInsuranceVariantPushedFromIndex;
    } catch {
      return false;
    }
  }

  private pushAbTestsForCurrentRoute(dataLayer: any): void {
    const tests = this.readAbTestsFromSession();
    if (!this.abSessionEnabled || tests.length === 0) {
      this.firstRouteHandled = true;
      return;
    }

    const isFirst = !this.firstRouteHandled;
    const skipFirst = isFirst && this.wasPushedFromIndex();
    if (!skipFirst) {
      for (const t of tests) {
        dataLayer.push({ event: t.event, [t.key]: t.value });
      }
    }
    this.firstRouteHandled = true;
  }

  private getTrackedPagePath(evt: NavigationEnd): string {
    const syncedCurrentUrl = this.partnerProviderContext.ensureCurrentUrlHasPartnerParams();
    if (syncedCurrentUrl) return syncedCurrentUrl;
    return this.partnerProviderContext.withPartnerQueryParams(evt.urlAfterRedirects || evt.url || '');
  }

  private pushCocmReferralPartnerEvent(dataLayer: any): void {
    const ready = this.partnerInitPromise || Promise.resolve();
    void ready
      .then(() => this.partnerProviderContext.ensureLoaded())
      .then(() => {
        const event = this.partnerProviderContext.getCocmReferralPartnerEvent();
        if (event) dataLayer.push(event);
      })
      .catch(() => {});
  }

  private completeBenchAfterTracking(evt: NavigationEnd): void {
    try {
      const targetUrl = typeof window !== 'undefined' && window.location
        ? `${window.location.origin}${window.location.pathname || ''}${window.location.search || ''}${window.location.hash || ''}`
        : (evt.urlAfterRedirects || evt.url);
      const pagePath = (evt.urlAfterRedirects || evt.url || '').toString().toLowerCase();
      const shouldDefer = pagePath.includes('/available-slots');
      if (shouldDefer) {
        this.bench.completePageTransition(targetUrl, true);
      } else {
        const sendBench = () => { try { this.bench.completePageTransition(targetUrl, false); } catch {} };
        if (typeof requestAnimationFrame === 'function') requestAnimationFrame(() => sendBench());
        else setTimeout(() => sendBench(), 0);
      }
    } catch {}
  }

  private beginInsuranceVariantStartupAssignment(): void {
    try {
      this.insuranceVariantService.ensureAssignmentCached('app_start').subscribe({
        next: (insuranceVariant) => {
          try {
            this.insuranceVariantService.preloadDetailsMeVariant(insuranceVariant?.version_code);
          } catch {}
        },
        error: () => {}
      });
    } catch {}
  }

  private ensureInsuranceVariantAssignmentForRoute(urlOrPath: string): void {
    try {
      const raw = (urlOrPath || '').toString();
      const path = (raw.split('?')[0].split('#')[0] || '/').toLowerCase();
      this.insuranceVariantService.ensureAssignmentCached(`route:${path}`).subscribe({
        next: (insuranceVariant) => {
          try {
            this.insuranceVariantService.preloadDetailsMeVariant(insuranceVariant?.version_code);
          } catch {}
        },
        error: () => {}
      });
    } catch {}
  }

  ngOnInit(): void {
    this.partnerInitPromise = this.partnerProviderContext.initializeFromUrl();
    this.memoTagInitPromise = this.memoTagContext.initializeFromUrl();
    this.applyInternalUserCookieFromUrl();
    this.detectUserLocation();
    this.beginInsuranceVariantStartupAssignment();

    this.variantAudit.startTracking();
    try {
      document.addEventListener('click', this.phoneClickListener, true);
    } catch {}

    // Only gate rendering + call variant assignment on scheduler/newpatient entry routes (including root redirect).
    try {
      const initialPath = (typeof window !== 'undefined' && window.location ? (window.location.pathname || '') : '') || '';
      if (this.isVariantGateRoute(initialPath)) {
        this.beginVariantBootstrapGate();
      } else {
        this.variantReady.set(true);
      }
    } catch {
      this.variantReady.set(true);
    }

    try {
      const fromUrl = this.readAbTestsFromUrl();
      if (fromUrl.length) {
        this.abSessionEnabled = true;
        this.storeAbTestsToSession(fromUrl);
      } else if (this.isInternalUser()) {
        this.abSessionEnabled = false;
        this.clearAbTestsFromSession();
      } else {
        const fromSession = this.readAbTestsFromSession();
        this.abSessionEnabled = fromSession.length > 0;
      }
    } catch {}
    this.ensureAbTestsQueryParamInUrl();
    
    // Push AB Test and page view events on every route
    this.routerSubscription = this.router.events
      .pipe(filter((e): e is NavigationStart | NavigationEnd | NavigationCancel | NavigationError =>
        e instanceof NavigationStart || e instanceof NavigationEnd || e instanceof NavigationCancel || e instanceof NavigationError
      ))
      .subscribe((evt: NavigationStart | NavigationEnd | NavigationCancel | NavigationError) => {
        if (evt instanceof NavigationStart) {
          try {
            const nextUrl = (evt.url || '').toString();
            if (this.isVariantGateRoute(nextUrl) && !this.variantInitStarted) {
              this.variantReady.set(false);
              this.beginVariantBootstrapGate();
            }
          } catch {}
          try {
            const fullCurrentUrl = typeof window !== 'undefined' && window.location
              ? `${window.location.origin}${window.location.pathname || ''}${window.location.search || ''}${window.location.hash || ''}`
              : '';
            this.bench.beginPageTransition(fullCurrentUrl);
          } catch {
            this.bench.cancelPendingPageTransition();
          }
          return;
        }

        if (evt instanceof NavigationCancel || evt instanceof NavigationError) {
          this.bench.cancelPendingPageTransition();
          return;
        }

        this.applyInternalUserCookieFromUrl();
        this.ensureAbTestsQueryParamInUrl();
        this.ensureInsuranceVariantAssignmentForRoute(evt.urlAfterRedirects || evt.url || '');

        try {
          const pagePathNow = (evt.urlAfterRedirects || evt.url || '').toString();
          if (this.isVariantGateRoute(pagePathNow) && !this.variantReady()) {
            this.pendingNavEnd = evt as NavigationEnd;
            return;
          }
        } catch {}

        if (!this.variantReady()) {
          this.pendingNavEnd = evt as NavigationEnd;
          return;
        }

        const dataLayer = (window as any).dataLayer = (window as any).dataLayer || [];
        const pagePath = this.getTrackedPagePath(evt);
        const variantSessionUuid = this.variantService.getSessionUuid();
        const ga4SessionId = this.variantService.getGa4SessionId() || variantSessionUuid;
        const partnerFields = this.partnerProviderContext.getDataLayerFields();
        if (!this.initialVariantEventsPushed) {
          dataLayer.push({ event: 'variant_session_id', session_id: ga4SessionId });
          const insuranceAssignment = this.insuranceVariantService.getCachedAssignment();
          if (insuranceAssignment) {
            dataLayer.push({ event: 'MobileViewSlots_AB_Test', heard_about_variant: this.extractInsuranceAbTestVariant(insuranceAssignment) });
          }
        }
        this.initialVariantEventsPushed = false;
        dataLayer.push({ event: 'page_view', page_path: pagePath, ...partnerFields });
        this.pushCocmReferralPartnerEvent(dataLayer);
        this.pushAbTestsForCurrentRoute(dataLayer);
        this.completeBenchAfterTracking(evt);
      });
  }

  private isVariantGateRoute(urlOrPath: string): boolean {
    try {
      const raw = (urlOrPath || '').toString().toLowerCase();
      const path = raw.split('?')[0].split('#')[0];
      return path === '/' || path === '' || path.startsWith('/scheduler') || path.startsWith('/newpatient');
    } catch {
      return false;
    }
  }

  private beginVariantBootstrapGate(): void {
    if (this.variantInitStarted) return;
    this.variantInitStarted = true;

    this.insuranceVariantService.ensureAssignmentCached('variant_bootstrap').subscribe({
      next: (insuranceVariant) => {
        const dataLayer = (window as any).dataLayer = (window as any).dataLayer || [];
        const variantSessionUuid = this.variantService.getSessionUuid();
        const ga4SessionId = this.variantService.getGa4SessionId() || variantSessionUuid;
        try {
          this.insuranceVariantService.preloadDetailsMeVariant(insuranceVariant?.version_code);
        } catch {}
        dataLayer.push({ event: 'variant_session_id', session_id: ga4SessionId });
        if (insuranceVariant && !this.wasInsuranceAbTestPushedFromIndex()) {
          dataLayer.push({ event: 'MobileViewSlots_AB_Test', heard_about_variant: this.extractInsuranceAbTestVariant(insuranceVariant) });
        }
        this.initialVariantEventsPushed = true;

        this.variantReady.set(true);

        const pending = this.pendingNavEnd;
        this.pendingNavEnd = null;
        if (pending) {
          try {
            const pagePath = this.getTrackedPagePath(pending);
            const partnerFields = this.partnerProviderContext.getDataLayerFields();
            dataLayer.push({ event: 'page_view', page_path: pagePath, ...partnerFields });
            this.pushCocmReferralPartnerEvent(dataLayer);
            this.pushAbTestsForCurrentRoute(dataLayer);
            this.completeBenchAfterTracking(pending);
          } catch {}
        }
      }
    });
  }

  private async detectUserLocation(): Promise<void> {
    try {
      const response = await fetch('https://ipapi.co/json/');
      if (!response.ok) {
        throw new Error(`ipapi.co responded with ${response.status}`);
      }
      const data = await response.json();
      
      const locationInfo = {
        ip: data.ip || 'Unknown',
        city: data.city || 'Unknown',
        state: data.region || 'Unknown',
        stateCode: data.region_code || 'Unknown',
        country: data.country_name || 'Unknown',
        countryCode: data.country_code || 'Unknown',
        postal: data.postal || 'Unknown',
        timezone: data.timezone || 'Unknown'
      };
      
      try {
        localStorage.setItem('user.detectedState', locationInfo.stateCode);
        localStorage.setItem('user.detectedStateFullName', locationInfo.state);
        localStorage.setItem('user.detectedCity', locationInfo.city);
        localStorage.setItem('user.detectedIP', locationInfo.ip);
      } catch (e) {
      }

      const statesToPrefetch = this.getStatesToPrefetch(locationInfo.stateCode);
      await this.prefetchSlotsForMultipleStates(statesToPrefetch);
      
    } catch {
      try {
        const fallbackResponse = await fetch('https://api.ipify.org?format=json');
        if (fallbackResponse.ok) {
          const fallbackData = await fallbackResponse.json();
          try {
            localStorage.setItem('user.detectedIP', fallbackData.ip || '');
          } catch {}
        }
      } catch {
      }
    }
  }

  private getStatesToPrefetch(detectedStateCode: string): string[] {
    const statesToPrefetch: string[] = [];
    const providingStateCodes = STATE_VISIT_CONFIG.map(s => s.code);
    
    if (providingStateCodes.includes(detectedStateCode)) {
      statesToPrefetch.push(detectedStateCode);
    }

    const neighbors = STATE_NEIGHBORS[detectedStateCode] || [];
    const nearestProvidingStates = neighbors.filter(n => providingStateCodes.includes(n));

    statesToPrefetch.push(...nearestProvidingStates.slice(0, 2));

    const uniqueStates = [...new Set(statesToPrefetch)];
    
    return uniqueStates;
  }

  private async prefetchSlotsForMultipleStates(stateCodes: string[]): Promise<void> {
    if (!stateCodes || stateCodes.length === 0) {
      return;
    }

    const allPrefetchPromises = stateCodes.map(stateCode => this.prefetchSlotsForState(stateCode));
    await Promise.all(allPrefetchPromises);
  }

  private async prefetchSlotsForState(stateCode: string): Promise<void> {
    try {
      const visitTypeCodes = getAllowedVisitTypeCodesForState(stateCode);
      if (visitTypeCodes.length === 0) {
        return;
      }

      const startDate = this.getStateTodayDateString(stateCode);
      const startTime = this.getCurrentTimeInStateTz(stateCode);

      const prefetchPromises = visitTypeCodes.map(async (visitTypeCode) => {
        const visitType = mapVisitTypeCodeToLabel(visitTypeCode);
        try {
          await this.providersCache.ensureForStateAndVisit(stateCode, visitType);
          const profileIds = this.providersCache.getProfileIds(stateCode, visitType);
          
          if (!Array.isArray(profileIds) || profileIds.length === 0) {
            return;
          }

          const existing = this.slotsCache.get(stateCode, startDate, visitType);
          if (existing) {
            return;
          }

          if (this.slotsCache.isFetching(stateCode, startDate, visitType)) {
            return;
          }

          const payload: any = {
            state: stateCode,
            visitType: visitType,
            startDate: startDate,
            startTime: startTime,
            profileIds: profileIds
          };

          return new Promise<void>((resolve) => {
            try { this.slotsCache.beginFetch(stateCode, startDate, visitType); } catch {}
            this.api.dispatch<any>('/providers/slots', payload).subscribe({
              next: (resp) => {
                try {
                  this.slotsCache.set(stateCode, startDate, visitType, resp);
                } catch {}
                try { this.slotsCache.endFetch(stateCode, startDate, visitType); } catch {}
                resolve();
              },
              error: () => {
                try { this.slotsCache.endFetch(stateCode, startDate, visitType); } catch {}
                resolve();
              }
            });
          });
        } catch {
          return Promise.resolve();
        }
      });

      await Promise.all(prefetchPromises);
    } catch {
    }
  }

  private getTimeZoneForState(stateCode: string): string {
    const tzMap: Record<string, string> = {
      'NC': 'America/New_York',
      'NY': 'America/New_York',
      'FL': 'America/New_York',
      'GA': 'America/New_York',
      'TN': 'America/Chicago',
      'MO': 'America/Chicago',
      'AR': 'America/Chicago',
      'IL': 'America/Chicago',
      'TX': 'America/Chicago',
      'OK': 'America/Chicago',
      'AZ': 'America/Phoenix',
      'WA': 'America/Los_Angeles',
      'CA': 'America/Los_Angeles',
      'OR': 'America/Los_Angeles'
    };
    return tzMap[stateCode.toUpperCase()] || 'America/New_York';
  }

  private getStateTodayDateString(stateCode: string): string {
    try {
      const tz = this.getTimeZoneForState(stateCode);
      const todayStr = new Intl.DateTimeFormat('en-CA', { 
        timeZone: tz, 
        year: 'numeric', 
        month: '2-digit', 
        day: '2-digit' 
      }).format(new Date());
      return todayStr;
    } catch {
      const t = new Date();
      const y = t.getFullYear();
      const m = String(t.getMonth() + 1).padStart(2, '0');
      const d = String(t.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    }
  }

  private getCurrentTimeInStateTz(stateCode: string): string {
    try {
      const tz = this.getTimeZoneForState(stateCode);
      const now = new Date();
      const fmt = new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
      const parts = fmt.formatToParts(now);
      const hour = parts.find(p => p.type === 'hour')?.value || '00';
      const minute = parts.find(p => p.type === 'minute')?.value || '00';
      return `${hour}:${minute}`;
    } catch {
      const now = new Date();
      return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    }
  }

  ngOnDestroy(): void {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
      this.routerSubscription = null;
    }
    try {
      document.removeEventListener('click', this.phoneClickListener, true);
    } catch {}
  }
}
